import { ApiError } from '@sd/contracts';
import { ContractError, NetworkError, type ApiClient } from './http';
import type { Platform } from './platform';
import type { ClientState, ClientStore } from './state';
import type { Timers } from './timers';

/** User-visible outcomes the UI turns into toasts or screens. */
export type ClientNotice =
  | { kind: 'sync.done'; flushed: number }
  | { kind: 'signed_out'; reason: 'revoked' | 'deactivated' | 'expired' | 'logged_out' }
  | { kind: 'upload.rejected'; name: string; reason: 'too_large' | 'not_allowed' | 'too_many' | 'infected' | 'failed' }
  /** A scheduled message could not go out (`code`: why), e.g. the conversation turned read-only. */
  | { kind: 'scheduled.failed'; conversationId: string; text: string; code: string }
  /** Files waiting for their scan went out by themselves; unscannable ones stayed in the tray for the sender to decide. */
  | { kind: 'upload.sent_when_ready'; sent: number; held: string[] }
  | { kind: 'download.done'; fileId: string; name: string }
  | { kind: 'download.failed'; fileId: string; name: string; code: string | null }
  | { kind: 'storage.unavailable' }
  | { kind: 'error'; code: string; context: string }
  /** Someone's profile changed (the store already shows it): for screens that keep their own copy of people. */
  | { kind: 'profile.updated'; userId: string };

export type SignOutReason = 'revoked' | 'deactivated' | 'expired';

/** One signed-in session's shared parts, handed to every module of the client. */
export interface ClientContext {
  readonly serverId: string;
  readonly api: ApiClient;
  readonly store: ClientStore;
  readonly platform: Platform;
  readonly timers: Timers;
  readonly now: () => Date;
  readonly newId: () => string;
  /** Is the window focused and visible? Used for read marking and notifications. */
  readonly isFocused: () => boolean;
  /** Set by stop(): running loops end and nothing new starts. */
  stopped: boolean;
  notice(notice: ClientNotice): void;
  signOut(reason: SignOutReason): void;
  updateBadge(): void;
}

/** Shorthands for the shared store, API and timers. */
export abstract class ClientModule {
  constructor(protected readonly ctx: ClientContext) {}

  protected get state(): ClientState {
    return this.ctx.store.getState();
  }

  protected set(patch: Partial<ClientState> | ((s: ClientState) => Partial<ClientState>)) {
    this.ctx.store.setState(patch as Partial<ClientState>);
  }

  protected get api(): ApiClient {
    return this.ctx.api;
  }

  protected get timers(): Timers {
    return this.ctx.timers;
  }

  protected get stopped(): boolean {
    return this.ctx.stopped;
  }
}

export function errorCode(error: unknown): string {
  if (error instanceof ApiError) return error.code;
  if (error instanceof NetworkError) return 'NETWORK';
  // The server answers in a newer contract than this app understands.
  if (error instanceof ContractError) return 'UNSUPPORTED_CLIENT';
  return 'INTERNAL';
}

export const SIGN_OUT_CODES = new Set(['UNAUTHENTICATED', 'SESSION_REVOKED', 'TOKEN_EXPIRED', 'ACCOUNT_DEACTIVATED']);

export function signOutReason(code: string): SignOutReason {
  return code === 'SESSION_REVOKED' ? 'revoked' : code === 'ACCOUNT_DEACTIVATED' ? 'deactivated' : 'expired';
}
